import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { HTTPError } from "ky";
import { rulesApi, humaniseViolation, } from "../../api/rules.api.js";
import { slotsApi } from "../../api/slots.api.js";
import { unwrapError } from "../../api/client.js";
import { ActionButton, GhostButton, MiniSpinner, colors, inputStyle } from "../ui/index.js";
import { useToast } from "../ui/ToastProvider.js";
import { venueShortName } from "../../lib/venue-names.js";
import { bulkBlocksOverlapping, maxBookableAt, blockedCourtCount } from "./bulk-warning.js";
import { BulkBookingGrid } from "./BulkBookingGrid.js";
const DAYS = [
    { day: 1, short: "Mon" },
    { day: 2, short: "Tue" },
    { day: 3, short: "Wed" },
    { day: 4, short: "Thu" },
    { day: 5, short: "Fri" },
    { day: 6, short: "Sat" },
    { day: 0, short: "Sun" },
];
function defaultState(initial, allowedVenues) {
    if (initial) {
        return {
            venueId: initial.venueId,
            dayOfWeek: initial.dayOfWeek,
            startTime: initial.startTime,
            durationMin: initial.durationMin,
            courtCount: initial.courtCount,
        };
    }
    return {
        venueId: allowedVenues[0] ?? "",
        dayOfWeek: 1,
        startTime: "18:00",
        durationMin: 60,
        courtCount: 1,
    };
}
function deriveRuleName(state) {
    const day = DAYS.find((d) => d.day === state.dayOfWeek)?.short ?? "?";
    return `${venueShortName(state.venueId)} · ${day} ${state.startTime}`;
}
function toMin(hhmm) {
    const [h, m] = hhmm.split(":");
    return Number(h) * 60 + Number(m);
}
/**
 * Fetches real BeWell slots for the selected venue + next occurrence of the
 * selected day-of-week, then renders the picker. Falls back to no chips while
 * loading.
 */
function SlotsSection({ venueId, dayOfWeek, windowsForSelectedDay, venueMaxCourts, startTime, durationMin, disabled, onChange, }) {
    const probeDate = useMemo(() => nextOccurrenceISO(dayOfWeek), [dayOfWeek]);
    const q = useQuery({
        queryKey: ["sidecar", "slots", venueId, probeDate],
        queryFn: () => slotsApi.list(venueId, probeDate),
        enabled: Boolean(venueId && probeDate),
        staleTime: 5 * 60 * 1000,
    });
    // Show every slot BeWell offers — don't filter by tenant windows.
    // Permission validation runs at submit time and surfaces a violation
    // if the chosen range falls outside allowed windows.
    const allSlots = useMemo(() => {
        if (!q.data)
            return [];
        return q.data.slots.map((s) => ({
            from: s.from,
            to: s.to,
            fromMin: toMin(s.from),
            durationMin: s.durationMin,
        }));
    }, [q.data]);
    // Set of allowed-window minute ranges so RealSlotPicker can grey out
    // slots that fall outside the tenant's permissions. Adjacent windows
    // (one ending exactly where the next starts) are coalesced so a slot
    // spanning two contiguous windows isn't falsely marked disallowed —
    // e.g. a 60m slot at 17:00 that crosses 17:30 between two pieces.
    // Group windows for the slot-grey check: merge ALL contiguous ranges
    // regardless of cap so a slot spanning two touching windows isn't
    // wrongly marked outside permissions.
    const allowedRanges = useMemo(() => mergeContiguousRanges(windowsForSelectedDay.map((w) => ({ fromMin: toMin(w.from), toMin: toMin(w.to) }))), [windowsForSelectedDay]);
    // For the visible "Allowed:" line: merge only when caps match, so the
    // user sees WHY a slot's cap drops mid-window (the merged display would
    // hide that the admin set different caps for sub-ranges).
    const allowedDisplayRanges = useMemo(() => mergeRangesByCap(windowsForSelectedDay.map((w) => ({
        fromMin: toMin(w.from),
        toMin: toMin(w.to),
        cap: w.maxCourts ?? venueMaxCourts,
    }))), [windowsForSelectedDay, venueMaxCourts]);
    const status = q.isLoading
        ? "Loading real slot times…"
        : q.error
            ? "Couldn't load slots from cache — check back in a minute."
            : allSlots.length === 0
                ? `No BeWell slots cached yet for ${venueShortName(venueId)} on this day.`
                : null;
    return (_jsxs("div", { style: { marginBottom: 16 }, children: [_jsx("span", { style: {
                    display: "block",
                    fontSize: "0.78rem",
                    fontWeight: 600,
                    color: colors.textSecondary,
                    marginBottom: 6,
                }, children: "Select your slot(s)" }), status ? (_jsx("p", { style: { margin: 0, fontSize: "0.78rem", color: colors.textTertiary }, children: status })) : (_jsx(RealSlotPicker, { slots: allSlots, allowedRanges: allowedRanges, startTime: startTime, durationMin: durationMin, disabled: disabled, onChange: onChange })), _jsxs("span", { style: {
                    display: "block",
                    fontSize: "0.7rem",
                    color: colors.textTertiary,
                    marginTop: 4,
                }, children: ["Allowed:", " ", allowedDisplayRanges
                        .map((r) => `${minToHHmm(r.fromMin)}–${minToHHmm(r.toMin)} (${r.cap} court${r.cap === 1 ? "" : "s"})`)
                        .join(", ")] })] }));
}
/**
 * The next future date that falls on the given JS day index (0 = Sun … 6 = Sat).
 * Returns YYYY-MM-DD for use as a slot-cache lookup key.
 */
function nextOccurrenceISO(targetDay) {
    const now = new Date();
    const today = now.getDay();
    const diff = (targetDay - today + 7) % 7 || 7;
    const dt = new Date(now.getFullYear(), now.getMonth(), now.getDate() + diff);
    const yyyy = dt.getFullYear();
    const mm = String(dt.getMonth() + 1).padStart(2, "0");
    const dd = String(dt.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
}
/**
 * Slot picker — single row of chips sorted by start time. Each chip is a
 * unique offering (start, duration). Click to select; click adjacent slots
 * whose edges touch to extend the selection contiguously across slot sizes
 * (40m + 60m = 100m).
 */
function RealSlotPicker({ slots, allowedRanges, startTime, durationMin, disabled, onChange, }) {
    const selFrom = toMin(startTime);
    const selTo = selFrom + durationMin;
    const sorted = [...slots].sort((a, b) => a.fromMin - b.fromMin || a.durationMin - b.durationMin);
    function handleClick(slot) {
        const slotFrom = slot.fromMin;
        const slotTo = slot.fromMin + slot.durationMin;
        if (durationMin === 0) {
            onChange(slot.from, slot.durationMin);
            return;
        }
        if (slotFrom >= selFrom &&
            slotTo <= selTo &&
            slot.durationMin < durationMin) {
            onChange(slot.from, slot.durationMin);
            return;
        }
        if (slotFrom === selTo) {
            onChange(startTime, slotTo - selFrom);
            return;
        }
        if (slotTo === selFrom) {
            onChange(slot.from, selTo - slotFrom);
            return;
        }
        onChange(slot.from, slot.durationMin);
    }
    function isWithinAllowed(fromMin, toMin) {
        if (!allowedRanges || allowedRanges.length === 0)
            return true;
        return allowedRanges.some((r) => fromMin >= r.fromMin && toMin <= r.toMin);
    }
    return (_jsxs("div", { role: "listbox", "aria-label": "Available slots", "aria-multiselectable": "true", style: {
            border: `1px solid ${colors.border}`,
            borderRadius: 8,
            padding: 10,
            background: colors.surface,
            display: "flex",
            flexWrap: "wrap",
            gap: 4,
        }, children: [sorted.map((slot) => {
                const slotFrom = slot.fromMin;
                const slotTo = slotFrom + slot.durationMin;
                const inSelection = slotFrom >= selFrom && slotTo <= selTo;
                const allowed = isWithinAllowed(slotFrom, slotTo);
                const tip = allowed
                    ? `${slot.from}–${slot.to} · ${slot.durationMin} min`
                    : `${slot.from}–${slot.to} · outside your allowed booking windows`;
                return (_jsxs("button", { type: "button", role: "option", "aria-selected": inSelection, "aria-disabled": !allowed, onClick: () => allowed && handleClick(slot), disabled: disabled || !allowed, title: tip, style: {
                        display: "inline-flex",
                        alignItems: "baseline",
                        gap: 6,
                        padding: "5px 10px",
                        borderRadius: 6,
                        border: `1px solid ${inSelection ? colors.primary : !allowed ? colors.divider : colors.border}`,
                        background: inSelection
                            ? colors.primary
                            : !allowed
                                ? colors.surfaceSecondary
                                : colors.surface,
                        color: inSelection
                            ? colors.textOnColor
                            : !allowed
                                ? colors.textTertiary
                                : colors.text,
                        fontSize: "0.82rem",
                        fontWeight: inSelection ? 600 : 500,
                        cursor: !allowed ? "not-allowed" : "pointer",
                        textDecoration: !allowed ? "line-through" : "none",
                        opacity: !allowed ? 0.6 : 1,
                        fontVariantNumeric: "tabular-nums",
                    }, children: [slot.from, _jsxs("span", { style: {
                                fontSize: "0.62rem",
                                fontWeight: 600,
                                opacity: 0.7,
                                letterSpacing: "0.02em",
                            }, children: [slot.durationMin, "m"] })] }, `${slot.fromMin}-${slot.durationMin}`));
            }), durationMin > 0 && (_jsxs("div", { style: {
                    marginTop: 8,
                    paddingTop: 8,
                    borderTop: `1px dashed ${colors.border}`,
                    display: "flex",
                    justifyContent: "space-between",
                    fontSize: "0.78rem",
                    color: colors.text,
                }, children: [_jsxs("span", { children: [_jsx("strong", { children: startTime }), " \u2192 ", _jsx("strong", { children: minToHHmm(selTo) })] }), _jsx("span", { style: { color: colors.textSecondary }, children: formatDuration(durationMin) })] }))] }));
}
/** Merge touching ranges only when their `cap` matches — split at cap boundaries. */
function mergeRangesByCap(ranges) {
    if (ranges.length <= 1)
        return [...ranges];
    const sorted = [...ranges].sort((a, b) => a.fromMin - b.fromMin);
    const out = [{ ...sorted[0] }];
    for (let i = 1; i < sorted.length; i++) {
        const cur = sorted[i];
        const last = out[out.length - 1];
        if (cur.fromMin <= last.toMin && cur.cap === last.cap) {
            if (cur.toMin > last.toMin)
                last.toMin = cur.toMin;
        }
        else {
            out.push({ ...cur });
        }
    }
    return out;
}
/** Coalesce touching ranges so [9-10, 10-11, 11-12] becomes [9-12]. */
function mergeContiguousRanges(ranges) {
    if (ranges.length <= 1)
        return [...ranges];
    const sorted = [...ranges].sort((a, b) => a.fromMin - b.fromMin);
    const out = [{ ...sorted[0] }];
    for (let i = 1; i < sorted.length; i++) {
        const cur = sorted[i];
        const last = out[out.length - 1];
        if (cur.fromMin <= last.toMin) {
            // Touching or overlapping — extend
            if (cur.toMin > last.toMin)
                last.toMin = cur.toMin;
        }
        else {
            out.push({ ...cur });
        }
    }
    return out;
}
function minToHHmm(min) {
    const h = Math.floor(min / 60);
    const m = min % 60;
    return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}
function formatDuration(min) {
    if (min < 60)
        return `${min} min`;
    const h = Math.floor(min / 60);
    const m = min % 60;
    if (m === 0)
        return `${h}h`;
    return `${h}h ${m}m`;
}
export function RuleForm({ mode, initial, permissions, onClose, onSaved }) {
    const { showToast } = useToast();
    const allowedVenues = useMemo(() => Object.entries(permissions.venues)
        .filter(([, v]) => v.allowed)
        .map(([id]) => id), [permissions]);
    const [state, setState] = useState(() => defaultState(initial, allowedVenues));
    const [submitting, setSubmitting] = useState(false);
    const [violations, setViolations] = useState([]);
    const [genericError, setGenericError] = useState(null);
    // Detected bulk bookings for the chosen venue (standing/recurring blocks).
    const bulkQuery = useQuery({
        queryKey: ["sidecar", "bulk-bookings", state.venueId],
        queryFn: () => rulesApi.bulkBookings(state.venueId),
        enabled: Boolean(state.venueId),
        staleTime: 30 * 60 * 1000,
    });
    const dayBlocks = bulkQuery.data?.byDay[state.dayOfWeek] ?? [];
    const ruleWindow = { dayOfWeek: state.dayOfWeek, startTime: state.startTime, durationMin: state.durationMin };
    const bulkOverlap = state.durationMin > 0 ? bulkBlocksOverlapping(dayBlocks, ruleWindow) : [];
    const bulkMax = maxBookableAt(dayBlocks, ruleWindow, bulkQuery.data?.totalCourts ?? 0);
    // Look up the venue permission case-insensitively (legacy data may have
    // mixed casing — see Permissions admin fix in commit d9a7690).
    const venuePerm = useMemo(() => {
        if (!state.venueId)
            return undefined;
        const want = state.venueId.toUpperCase();
        return Object.entries(permissions.venues).find(([id]) => id.toUpperCase() === want)?.[1];
    }, [permissions, state.venueId]);
    // Per-window `maxCourts` is the only court cap; windows without an explicit
    // value allow a single court. This constant is the fallback for such windows.
    const maxCourts = 1;
    // Highest court cap across windows overlapping the current selection.
    // With partial-eligibility (validator change fcd2542) the user may
    // request up to this many — windows with smaller caps will simply
    // reduce automatically (warned about in the heads-up panel below).
    const selectedSlotCap = useMemo(() => {
        if (state.durationMin === 0)
            return maxCourts;
        const wins = venuePerm?.windowsByDay[state.dayOfWeek] ?? [];
        const selFrom = toMin(state.startTime);
        const selTo = selFrom + state.durationMin;
        let cap = 0;
        for (const w of wins) {
            const wFrom = toMin(w.from);
            const wTo = toMin(w.to);
            if (selFrom < wTo && selTo > wFrom) {
                const wCap = w.maxCourts ?? maxCourts;
                if (wCap > cap)
                    cap = wCap;
            }
        }
        return cap === 0 ? maxCourts : cap;
    }, [state.startTime, state.durationMin, state.dayOfWeek, venuePerm, maxCourts]);
    // Per-window slot plan for the current draft. Mirrors the backend
    // `planRuleSlots` so we can warn the user UP FRONT when their requested
    // courtCount will be reduced across sub-ranges (e.g. 19:00-19:30 cap 2,
    // 19:30-20:00 cap 1 → "we'll book 2+1 = 3 slots, not 2 × 60min"). Empty
    // when the form isn't fully specified yet.
    const slotPlan = useMemo(() => {
        if (!venuePerm || state.durationMin === 0) {
            return { ranges: [], reduced: false, totalSlots: 0 };
        }
        const wins = venuePerm.windowsByDay[state.dayOfWeek] ?? [];
        const ruleFrom = toMin(state.startTime);
        const ruleTo = ruleFrom + state.durationMin;
        const ranges = [];
        for (const w of wins) {
            const from = Math.max(ruleFrom, toMin(w.from));
            const to = Math.min(ruleTo, toMin(w.to));
            if (from >= to)
                continue;
            const cap = Math.min(state.courtCount, w.maxCourts ?? maxCourts);
            if (cap > 0)
                ranges.push({ fromMin: from, toMin: to, cap });
        }
        ranges.sort((a, b) => a.fromMin - b.fromMin);
        const covers = (() => {
            if (ranges.length === 0)
                return false;
            let cursor = ruleFrom;
            for (const r of ranges) {
                if (r.fromMin > cursor)
                    return false;
                if (r.toMin > cursor)
                    cursor = r.toMin;
                if (cursor >= ruleTo)
                    return true;
            }
            return cursor >= ruleTo;
        })();
        const reduced = ranges.length === 0 ||
            ranges.some((r) => r.cap < state.courtCount) ||
            !covers;
        const totalSlots = ranges.reduce((sum, r) => sum + r.cap, 0);
        return { ranges, reduced, totalSlots };
    }, [venuePerm, state.startTime, state.durationMin, state.dayOfWeek, state.courtCount, maxCourts]);
    // Day-level windows for the currently selected venue. Drives day chip
    // enable/disable + the start-time hint below.
    const allowedDays = useMemo(() => {
        if (!venuePerm)
            return new Set();
        const out = new Set();
        for (const [dayKey, wins] of Object.entries(venuePerm.windowsByDay)) {
            if (wins && wins.length > 0) {
                out.add(Number(dayKey));
            }
        }
        return out;
    }, [venuePerm]);
    const windowsForSelectedDay = useMemo(() => {
        if (!venuePerm)
            return [];
        return venuePerm.windowsByDay[state.dayOfWeek] ?? [];
    }, [venuePerm, state.dayOfWeek]);
    // Drag-aware close: only dismiss if the user STARTED on the backdrop.
    // Without this, click+drag inside the dialog (e.g. text selection) that
    // ends outside fires a click on the overlay and closes the modal — a
    // common cause of "I lost my work" complaints.
    const downOnBackdropRef = useRef(false);
    const update = (key, value) => setState((s) => ({ ...s, [key]: value }));
    const handleSubmit = async (e) => {
        e.preventDefault();
        setViolations([]);
        setGenericError(null);
        if (!state.venueId) {
            setGenericError("Pick a venue.");
            return;
        }
        if (state.durationMin <= 0) {
            setGenericError("Pick a duration by selecting one or more slots.");
            return;
        }
        // No upper cap — the only limit is that the session stays within the same
        // day (start + duration must not cross midnight).
        if (toMin(state.startTime) + state.durationMin > 24 * 60) {
            setGenericError("The session must end on the same day it starts.");
            return;
        }
        if (state.courtCount < 1 || state.courtCount > selectedSlotCap) {
            setGenericError(`Court count must be between 1 and ${selectedSlotCap} for this slot.`);
            return;
        }
        const body = {
            // Auto-derived from venue + day + time so the user never sees a "Name"
            // field. Easy to change later if we expose a name override.
            name: deriveRuleName(state),
            venueId: state.venueId,
            dayOfWeek: state.dayOfWeek,
            startTime: state.startTime,
            durationMin: state.durationMin,
            courtCount: state.courtCount,
        };
        setSubmitting(true);
        try {
            if (mode === "create") {
                await rulesApi.create(body);
                showToast("Preference created", "success");
            }
            else if (initial) {
                await rulesApi.update(initial.id, body);
                showToast("Preference updated", "success");
            }
            onSaved();
        }
        catch (err) {
            if (err instanceof HTTPError && err.response.status === 422) {
                try {
                    const parsed = (await err.response.json());
                    const vs = parsed.error?.violations ?? [];
                    if (vs.length > 0) {
                        setViolations(vs);
                    }
                    else {
                        setGenericError(parsed.error?.message ?? "Preference violates current permissions.");
                    }
                    return;
                }
                catch {
                    // fall through to generic
                }
            }
            const apiErr = await unwrapError(err);
            setGenericError(apiErr.message);
        }
        finally {
            setSubmitting(false);
        }
    };
    return (_jsx("div", { role: "dialog", "aria-modal": "true", "aria-label": mode === "create" ? "Create booking preference" : "Edit booking preference", style: {
            position: "fixed",
            inset: 0,
            background: "rgba(15, 23, 42, 0.45)",
            display: "grid",
            placeItems: "center",
            zIndex: 1000,
            padding: 16,
        }, onMouseDown: (e) => {
            downOnBackdropRef.current = e.target === e.currentTarget;
        }, onMouseUp: (e) => {
            // Close only when both press AND release happened on the backdrop.
            // Prevents click+drag inside the dialog (text selection, slider
            // drag, etc.) from accidentally dismissing.
            if (downOnBackdropRef.current &&
                e.target === e.currentTarget &&
                !submitting) {
                onClose();
            }
            downOnBackdropRef.current = false;
        }, children: _jsxs("div", { style: {
                background: colors.surface,
                borderRadius: 12,
                padding: 24,
                maxWidth: 480,
                width: "100%",
                maxHeight: "90vh",
                overflowY: "auto",
                boxShadow: colors.shadowLg,
            }, children: [_jsx("h3", { style: { marginTop: 0, marginBottom: 16 }, children: mode === "create" ? "Create booking preference" : "Edit booking preference" }), _jsxs("form", { onSubmit: (e) => void handleSubmit(e), children: [_jsx(Field, { label: "Venue", id: "rule-venue", children: allowedVenues.length === 0 ? (_jsx("span", { style: { color: colors.danger, fontSize: "0.85rem" }, children: "No venues are currently allowed by your permissions." })) : (_jsx("select", { id: "rule-venue", value: state.venueId, onChange: (e) => update("venueId", e.target.value), disabled: submitting, style: inputStyle, children: allowedVenues.map((id) => (_jsx("option", { value: id, children: venueShortName(id) }, id))) })) }), _jsx(Field, { label: "Day of week", id: "rule-day", children: _jsx("div", { role: "radiogroup", "aria-label": "Day of week", style: { display: "flex", flexWrap: "wrap", gap: 6 }, children: DAYS.map(({ day, short }) => {
                                    const active = state.dayOfWeek === day;
                                    const dayAllowed = allowedDays.size === 0 || allowedDays.has(day);
                                    const disabled = submitting || !dayAllowed;
                                    return (_jsx("button", { type: "button", role: "radio", "aria-checked": active, onClick: () => update("dayOfWeek", day), disabled: disabled, title: dayAllowed ? short : "Not in your allowed windows for this venue", style: {
                                            padding: "6px 12px",
                                            borderRadius: 999,
                                            border: `1px solid ${active ? colors.primary : colors.border}`,
                                            background: active ? colors.primary : colors.surface,
                                            color: active
                                                ? colors.textOnColor
                                                : dayAllowed
                                                    ? colors.text
                                                    : colors.textTertiary,
                                            fontSize: "0.8rem",
                                            cursor: disabled ? "not-allowed" : "pointer",
                                            fontWeight: active ? 600 : 400,
                                            opacity: dayAllowed ? 1 : 0.5,
                                        }, children: short }, day));
                                }) }) }), windowsForSelectedDay.length > 0 && (_jsx(SlotsSection, { venueId: state.venueId, dayOfWeek: state.dayOfWeek, windowsForSelectedDay: windowsForSelectedDay, venueMaxCourts: maxCourts, startTime: state.startTime, durationMin: state.durationMin, disabled: submitting, onChange: (startTime, durationMin) => {
                                setState((s) => ({ ...s, startTime, durationMin }));
                            } })), state.venueId && dayBlocks.length > 0 && (_jsxs("div", { style: {
                                background: colors.surfaceSecondary,
                                padding: 10,
                                borderRadius: 6,
                                fontSize: "0.78rem",
                                marginBottom: 12,
                            }, children: [_jsxs("strong", { style: { color: colors.textTertiary }, children: ["Usually unavailable on ", DAYS.find((d) => d.day === state.dayOfWeek)?.short ?? "this day", " at ", venueShortName(state.venueId), ":"] }), _jsx("div", { style: { marginTop: 6 }, children: _jsx(BulkBookingGrid, { dayBlocks: dayBlocks, totalCourts: bulkQuery.data?.totalCourts ?? 0, ruleWindow: ruleWindow }) })] })), bulkOverlap.length > 0 && (_jsxs("div", { role: "status", style: {
                                background: colors.warningLight,
                                color: colors.warning,
                                padding: 10,
                                borderRadius: 6,
                                fontSize: "0.82rem",
                                marginBottom: 12,
                            }, children: [_jsx("strong", { children: "Heads up \u2014 this time is block-booked." }), _jsxs("p", { style: { margin: "4px 0 0 0" }, children: ["\u2248", blockedCourtCount(bulkOverlap), " of ", bulkQuery.data?.totalCourts ?? "?", " courts here are block-booked every ", DAYS.find((d) => d.day === state.dayOfWeek)?.short ?? "week", " \u2014 you'll likely get at most ", _jsx("strong", { children: bulkMax }), " court", bulkMax === 1 ? "" : "s", "."] })] })), _jsx(Field, { label: `Court count (up to ${selectedSlotCap})`, id: "rule-courts", children: _jsx("input", { id: "rule-courts", type: "number", min: 1, max: selectedSlotCap, value: state.courtCount, onChange: (e) => update("courtCount", Number(e.target.value)), disabled: submitting, style: inputStyle }) }), slotPlan.reduced && slotPlan.ranges.length > 0 && state.courtCount > 0 && (_jsxs("div", { role: "status", style: {
                                background: colors.warningLight,
                                color: colors.warning,
                                padding: 10,
                                borderRadius: 6,
                                fontSize: "0.82rem",
                                marginBottom: 12,
                            }, children: [_jsx("strong", { children: "Heads up \u2014 your booking will be reduced." }), _jsxs("p", { style: { margin: "4px 0 6px 0" }, children: ["You asked for ", state.courtCount, " court", state.courtCount === 1 ? "" : "s", " across", " ", state.startTime, "\u2013", minToHHmm(toMin(state.startTime) + state.durationMin), ", but your permitted windows allow:"] }), _jsx("ul", { style: { margin: "0 0 4px 0", paddingLeft: 18 }, children: slotPlan.ranges.map((r, i) => (_jsxs("li", { children: [_jsxs("strong", { children: [minToHHmm(r.fromMin), "\u2013", minToHHmm(r.toMin)] }), ": ", r.cap, " court", r.cap === 1 ? "" : "s", r.cap < state.courtCount && (_jsxs("span", { style: { opacity: 0.8 }, children: [" (reduced from ", state.courtCount, ")"] }))] }, i))) }), _jsxs("p", { style: { margin: "4px 0 0 0", fontSize: "0.78rem", opacity: 0.85 }, children: ["Total: ", slotPlan.totalSlots, " slot", slotPlan.totalSlots === 1 ? "" : "s", " per occurrence."] })] })), violations.length > 0 && (_jsxs("div", { role: "alert", style: {
                                background: colors.dangerLight,
                                color: colors.danger,
                                padding: 10,
                                borderRadius: 6,
                                fontSize: "0.85rem",
                                marginBottom: 12,
                            }, children: [_jsx("strong", { children: "This preference violates your current permissions:" }), _jsx("ul", { style: { margin: "6px 0 0 0", paddingLeft: 18 }, children: violations.map((v, i) => (_jsx("li", { children: humaniseViolation(v) }, `${v.code}-${i}`))) })] })), genericError && (_jsx("div", { role: "alert", style: {
                                background: colors.dangerLight,
                                color: colors.danger,
                                padding: 10,
                                borderRadius: 6,
                                fontSize: "0.85rem",
                                marginBottom: 12,
                            }, children: genericError })), _jsxs("div", { style: {
                                display: "flex",
                                gap: 8,
                                justifyContent: "flex-end",
                                marginTop: 16,
                            }, children: [_jsx(GhostButton, { onClick: onClose, disabled: submitting, children: "Cancel" }), _jsx(ActionButton, { type: "submit", disabled: submitting || allowedVenues.length === 0, children: submitting ? (_jsxs(_Fragment, { children: [_jsx(MiniSpinner, { size: 12, color: colors.surface }), " Saving"] })) : mode === "create" ? ("Add preference") : ("Save changes") })] })] })] }) }));
}
function Field({ label, id, children }) {
    return (_jsxs("label", { htmlFor: id, style: { display: "block", marginBottom: 12 }, children: [_jsx("span", { style: {
                    display: "block",
                    fontSize: "0.8rem",
                    fontWeight: 600,
                    marginBottom: 4,
                    color: colors.text,
                }, children: label }), children] }));
}
